import { Timestamp } from 'firebase/firestore';

/**
 * Formats a timestamp into a relative time string like "Now", "1 min ago", "2 hrs ago", "Yesterday", or "11 Nov"
 * @param timestamp - Can be a Date object, Firestore Timestamp, or number (milliseconds)
 * @returns A formatted string representing the relative time
 */
export function formatRelativeTime(timestamp: Date | Timestamp | number | any): string {
    // Convert to Date object
    let date: Date;

    if (timestamp instanceof Date) {
        date = timestamp;
    } else if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        // Firestore Timestamp
        date = timestamp.toDate();
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        return '';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHrs = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHrs / 24);

    // Less than 1 minute
    if (diffSecs < 60) {
        return 'Now';
    }

    // Less than 1 hour
    if (diffMins < 60) {
        return diffMins === 1 ? '1 min ago' : `${diffMins} mins ago`;
    }

    // Less than 24 hours
    if (diffHrs < 24) {
        return diffHrs === 1 ? '1 hr ago' : `${diffHrs} hrs ago`;
    }

    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear()
    ) {
        return 'Yesterday';
    }

    // More than yesterday - format as "11 Nov" or "11 Nov 2023" for different years
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];

    // If different year, include year
    if (date.getFullYear() !== now.getFullYear()) {
        return `${day} ${month} ${date.getFullYear()}`;
    }

    return `${day} ${month}`;
}
